import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, MapPin, RefreshCw } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { Field, inputClass } from '../../../../components/ui/Field';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ProgressBar } from '../../../../components/ui/ProgressBar';
import { CandidatesSheet } from '../../components/CandidatesSheet';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { QuantityStepper } from '../../components/QuantityStepper';
import { openDatePicker } from '../../components/openDatePicker';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW } from '../../../../features/operator/operatorConstants';
import { countTarget, unitCode, unitsLabel } from '../../../../features/operator/operatorCodes';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import {
  chooseReceivingLine,
  closeReceipt,
  confirmExcessCount,
  countReceipt,
  fetchReceipt,
  lostCountOf,
  repeatLastCount,
  scanReceivingCode,
  submitCount,
} from '../../../../features/operatorReceiving/operatorReceivingThunks';
import {
  receivingDraftCancelled,
  receivingEntered,
  receivingLeft,
  receivingResolutionDismissed,
  receivingSlotChangeRequested,
  selectReceivingAccepting,
} from '../../../../features/operatorReceiving/operatorReceivingSlice';
import {
  EXCESS_REVIEW_LABEL,
  OPEN_RECEIPT_STATUSES,
  RECEIVING_STEP,
  countProblem,
  excessIsLarge,
  excessOf,
  excessText,
  lineMissing,
  shortageMessage,
} from '../../../../features/operatorReceiving/operatorReceivingUtils';
import { unitsOf } from '../../../../features/receipts/receiptsUtils';
import { formatQuantity, receiptStatusLabel } from '../../../../utils/inventoryLabels';

/**
 * How much of a line arrived, counted from 0 up to what the line lacks:
 * «0 / 24». More can arrive: past it, the count turns amber and asks before
 * registering. Seeded once per scanned code — remounted by key — so a reload
 * of the receipt does not overwrite what is being typed. `line` is the
 * counted line as the receipt on screen has it now.
 */
const CountForm = ({ draft, line, receiptId, slotCode, quantityRef, onCancel }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState({ quantity: '0', lot: '', expiryDate: '' });
  const problem = countProblem(values, draft);
  const goal = line ? countTarget(lineMissing(line), draft.factor) : null;
  const set = (key) => (value) => setValues((prev) => ({ ...prev, [key]: value }));

  const count = {
    receiptId,
    code: draft.code,
    lineId: draft.lineId,
    factor: draft.factor,
    quantity: Number(values.quantity),
    lot: draft.lotRequired ? draft.lockedLot || values.lot.trim() : undefined,
    expiryDate: draft.expiryRequired ? draft.lockedExpiry || values.expiryDate : undefined,
    slotCode: slotCode || undefined,
  };
  // The count whose answer was lost goes again as it went; any other over what the line lacks asks first.
  const resends = useSelector((state) => Boolean(lostCountOf(state.operatorReceiving, count)));
  const over = !resends && excessOf(values, { factor: draft.factor, missing: lineMissing(line) }) > 0;

  return (
    <div data-testid="receiving-count-form" className="space-y-5 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
      <div className="text-center">
        <p className="text-lg font-bold text-ninja-dark dark:text-white">{draft.sku}</p>
        <p className="text-sm text-text-secondary">{draft.name}</p>
        {draft.factor > 1 && (
          <p className="mt-1 text-sm font-semibold text-ninja-green">1 caja = {formatQuantity(draft.factor)} un</p>
        )}
      </div>

      <QuantityStepper
        label="¿Cuántos llegaron?"
        value={values.quantity}
        onChange={set('quantity')}
        min={0}
        allowDecimals={draft.allowsFraction}
        target={goal ?? undefined}
        unit={draft.factor > 1 ? unitsLabel(values.quantity, draft.factor) : 'unidades'}
        inputRef={quantityRef}
      />

      {draft.lotRequired &&
        (draft.lockedLot ? (
          <p className="text-sm text-text-secondary">Lote {draft.lockedLot}</p>
        ) : (
          <Field label="Lote" htmlFor="receiving-lot">
            <input
              id="receiving-lot"
              data-testid="receiving-lot"
              value={values.lot}
              onChange={(event) => set('lot')(event.target.value)}
              autoComplete="off"
              className={`${inputClass} h-12 text-base`}
            />
          </Field>
        ))}

      {draft.expiryRequired &&
        (draft.lockedExpiry ? (
          <p className="text-sm text-text-secondary">Vence {draft.lockedExpiry}</p>
        ) : (
          <Field label="Vencimiento" htmlFor="receiving-expiry">
            <input
              id="receiving-expiry"
              data-testid="receiving-expiry"
              type="date"
              value={values.expiryDate}
              onChange={(event) => set('expiryDate')(event.target.value)}
              onClick={openDatePicker}
              className={`${inputClass} h-12 text-base dark:[color-scheme:dark]`}
            />
          </Field>
        ))}

      <OperatorActionBar>
        <Button variant="ghost" size="lg" onClick={onCancel}>
          Cancelar
        </Button>
        <PendingButton
          pendingOn={countReceipt}
          pendingKey={receiptId}
          variant="primary"
          size="lg"
          disabled={Boolean(problem)}
          loadingLabel="Registrando…"
          onClick={() => dispatch(submitCount(count))}
          data-testid="receiving-count-submit"
        >
          {problem || (over ? EXCESS_REVIEW_LABEL : `Registrar ${unitsLabel(values.quantity, draft.factor)}`)}
        </PendingButton>
      </OperatorActionBar>
    </div>
  );
};

/**
 * A line a count goes over, as the sheet shows it: what was expected, what
 * still lacks, what goes in and how much over.
 *
 * @param {object} props
 * @param {object} props.item - As the 409 `over_receipt` lists it.
 * @param {string} [props.name] - The product's, from the receipt on screen when the 409 does not bring it.
 * @param {{quantity: number, factor: number}} [props.count] - The count typed, when it is this line's.
 */
const ExcessDetail = ({ item, name, count }) => {
  const large = excessIsLarge(item);
  return (
    <li
      data-testid="receiving-excess-detail"
      data-line-id={item.line_id}
      data-excess={item.excess}
      data-large={String(large)}
      className="space-y-2 px-4 py-3"
    >
      <p className="font-semibold text-ninja-dark dark:text-white">{[item.sku, name].filter(Boolean).join(' — ')}</p>
      <p className="text-ninja-dark dark:text-white">{excessText(item, count)}</p>
      {large && (
        <p className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle size={18} aria-hidden="true" className="shrink-0" />
          ¿Seguro? Es más del doble de lo esperado.
        </p>
      )}
    </li>
  );
};

/**
 * A line of the receipt, how much arrived of what was expected. While a
 * product is asked for, tapping it reads it as scanning its unit would.
 */
const LineRow = ({ line, choosable, disabled, onTap }) => {
  const complete = Number(line.received) >= Number(line.expected);
  const content = (
    <span className="flex items-center gap-3">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-ninja-dark dark:text-white">{line.product_sku}</span>
        <span className="block truncate text-sm font-normal text-text-secondary">{line.product_name}</span>
      </span>
      <span className="shrink-0 text-sm font-semibold text-ninja-dark dark:text-white">
        {formatQuantity(line.received)} / {formatQuantity(line.expected)}
      </span>
      {complete && <CheckCircle2 size={20} className="shrink-0 text-ninja-green" />}
    </span>
  );
  const code = unitCode(line.codes, line.product_sku);
  return (
    <li data-testid="receipt-line" data-line-id={line.id} data-complete={String(complete)}>
      {choosable ? (
        <Button
          variant="choice"
          disabled={disabled}
          onClick={() => onTap(code, line.id)}
          data-testid="receipt-line-choose"
          data-line-id={line.id}
          data-code={code}
        >
          {content}
        </Button>
      ) : (
        <div className="rounded-2xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">{content}</div>
      )}
    </li>
  );
};

/**
 * One receipt, counted on the floor: where the goods land (when that has to
 * be said), then product by product, each with how many arrived.
 */
export const ReceivingCount = () => {
  const { receiptId } = useParams();
  const id = Number(receiptId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const receiving = useSelector((state) => state.operatorReceiving);
  const accepting = useSelector(selectReceivingAccepting);
  const quantityRef = useRef(null);
  const { receipt, loading, error, step, draft, slotCode, pending, resolution, lastCount } = receiving;

  useEffect(() => {
    dispatch(receivingEntered(id));
    dispatch(taskEntered({ flow: OPERATOR_FLOW.RECEIVING, taskId: id }));
    dispatch(fetchReceipt(id));
    return () => {
      dispatch(receivingLeft());
    };
  }, [dispatch, id]);

  // Back to the queue only from this receipt: the answer can come once the operator left it.
  const close = async (confirmShortage = false) => {
    const action = await dispatch(closeReceipt({ receiptId: id, confirmShortage }));
    if (closeReceipt.fulfilled.match(action) && !action.meta.offScreenTask) navigate('/receiving');
  };

  const read = (code, source, alternatives) => dispatch(scanReceivingCode({ code, source, alternatives }));
  const atSlot = step === RECEIVING_STEP.SLOT;

  // «Corregir la cantidad»: back on the number, selected, to type it again.
  const correctCount = () => {
    dispatch(receivingResolutionDismissed());
    quantityRef.current?.focus();
    quantityRef.current?.select();
  };

  if (!receipt && loading) {
    return (
      <div className="flex justify-center py-16" data-testid="receiving-loading">
        <Loader2 size={32} className="animate-spin text-ninja-green" />
      </div>
    );
  }
  if (!receipt && error) {
    return (
      <ErrorState title="No pudimos abrir la recepción" description={error.message} onRetry={() => dispatch(fetchReceipt(id))}>
        <Button variant="ghost" onClick={() => navigate('/receiving')}>
          Volver
        </Button>
      </ErrorState>
    );
  }
  if (!receipt) return null;

  const units = unitsOf(receipt);
  const open = OPEN_RECEIPT_STATUSES.includes(receipt.status);
  const slotName =
    receipt.slot_name ||
    (receipt.receiving_slots || []).find((slot) => slot.code === slotCode)?.name ||
    slotCode;
  const canChangeSlot = open && !receipt.slot_locked && !receipt.slot && (receipt.receiving_slots?.length ?? 0) > 1;
  const lastLine = lastCount && receipt.lines.find((line) => line.id === lastCount.lineId);
  const lineOf = (lineId) => receipt.lines.find((line) => line.id === lineId);
  const overCount = resolution?.kind === 'excess' ? resolution : null;

  return (
    <div className="space-y-5" data-testid="receiving-count" data-step={step}>
      <section className="space-y-3 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xl font-bold text-ninja-dark dark:text-white">{receipt.code}</p>
            <p className="text-sm text-text-secondary">
              {[receipt.company_name, receipt.supplier, receipt.reference].filter(Boolean).join(' · ')}
            </p>
          </div>
          <span className="shrink-0 rounded-full border border-border px-2.5 py-0.5 text-xs font-semibold text-text-secondary">
            {receiptStatusLabel(receipt.status)}
          </span>
        </div>
        <ProgressBar
          testId="receipt-progress"
          value={units.received}
          max={units.expected}
          label={`${formatQuantity(units.received)} de ${formatQuantity(units.expected)} un${
            units.missing > 0 ? ` · faltan ${formatQuantity(units.missing)}` : ''
          }`}
        />
        {slotName && (
          <div className="flex items-center justify-between gap-3">
            <p data-testid="receipt-slot" className="flex items-center gap-1.5 text-sm text-text-secondary">
              <MapPin size={16} /> {slotName}
            </p>
            {canChangeSlot && step !== RECEIVING_STEP.SLOT && (
              <Button variant="ghost" size="lg" onClick={() => dispatch(receivingSlotChangeRequested())}>
                Cambiar
              </Button>
            )}
          </div>
        )}
      </section>

      {error && (
        <div data-testid="receiving-reload-error" role="alert">
          <ErrorState variant="inline" title="No pudimos recargar la recepción; puede no estar al día." description={error.message}>
            <PendingButton
              pendingOn={fetchReceipt}
              pendingKey={id}
              variant="secondary"
              size="lg"
              fullWidth
              icon={RefreshCw}
              loadingLabel="Recargando…"
              onClick={() => dispatch(fetchReceipt(id))}
              data-testid="receiving-reload-error-retry"
            >
              Recargar la recepción
            </PendingButton>
          </ErrorState>
        </div>
      )}

      {!open && (
        <p data-testid="receipt-closed" className="rounded-2xl border border-border bg-white p-4 text-center font-semibold text-ninja-dark dark:bg-[#1a1a1a] dark:text-white">
          Esta recepción ya está {receiptStatusLabel(receipt.status).toLowerCase()}.
        </p>
      )}

      {open && step === RECEIVING_STEP.QUANTITY && draft && (
        <CountForm
          key={`${draft.lineId}:${draft.code}`}
          draft={draft}
          line={lineOf(draft.lineId)}
          receiptId={id}
          slotCode={slotCode}
          quantityRef={quantityRef}
          onCancel={() => dispatch(receivingDraftCancelled())}
        />
      )}

      {/* One camera for the slot and the products, as on the picking walk. */}
      {open && (atSlot || step === RECEIVING_STEP.PRODUCT) && (
        <section className="space-y-3">
          <OperatorScanPanel
            prompt={atSlot ? '¿Dónde dejas lo que llega?' : 'Escanea un producto'}
            hint={
              atSlot
                ? 'Escanea la ubicación de recepción, o tócala.'
                : 'Su código de barras, su SKU o el de su caja. O toca su línea abajo.'
            }
            accepting={accepting}
            lastCode={receiving.scan.lastCode}
            rereadOn={atSlot ? null : `product:${slotCode}`}
            busyLabel="Registrando…"
            onCode={read}
            repeatLabel={
              !atSlot && lastLine
                ? `Contar otra igual · ${unitsLabel(lastCount.quantity, lastCount.factor)} ${lastLine.product_sku}`
                : undefined
            }
            onRepeat={() => dispatch(repeatLastCount())}
          />
          {atSlot && (
            <div className="grid grid-cols-2 gap-2">
              {receipt.receiving_slots.map((slot) => (
                <Button
                  key={slot.id}
                  variant="outline"
                  size="lg"
                  onClick={() => read(slot.code, SCAN_SOURCE.TAP)}
                  data-testid="receiving-slot-option"
                >
                  {slot.name || slot.code}
                </Button>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-text-secondary">Líneas</h2>
        <ul className="space-y-2">
          {receipt.lines.map((line) => (
            <LineRow
              key={line.id}
              line={line}
              choosable={open && step === RECEIVING_STEP.PRODUCT}
              disabled={!accepting}
              onTap={(code, lineId) => dispatch(scanReceivingCode({ code, source: SCAN_SOURCE.TAP, lineId }))}
            />
          ))}
        </ul>
      </section>

      {open && step !== RECEIVING_STEP.QUANTITY && (
        <OperatorActionBar>
          <PendingButton
            pendingOn={closeReceipt}
            pendingKey={id}
            variant="secondary"
            size="lg"
            loadingLabel="Cerrando…"
            disabled={Boolean(pending)}
            onClick={() => close(false)}
            data-testid="receiving-close"
          >
            Cerrar recepción
          </PendingButton>
        </OperatorActionBar>
      )}

      <CandidatesSheet
        isOpen={resolution?.kind === 'ambiguous'}
        message={resolution?.message}
        options={resolution?.options}
        onChoose={(lineId) => dispatch(chooseReceivingLine(lineId))}
        onClose={() => dispatch(receivingResolutionDismissed())}
        busy={pending === 'count'}
      />

      <ConfirmSheet
        isOpen={resolution?.kind === 'confirm'}
        title="¿Cerrar con faltante?"
        message={resolution?.kind === 'confirm' ? shortageMessage(resolution.missing) : undefined}
        items={(resolution?.missing || []).map((line) => ({
          key: line.line_id,
          title: `${line.sku} — ${line.name}`,
          subtitle: `Llegaron ${formatQuantity(line.received)} de ${formatQuantity(line.expected)} · faltan ${formatQuantity(line.missing)}`,
        }))}
        confirmLabel="Cerrar con faltante"
        busy={pending === 'close'}
        busyLabel="Cerrando…"
        onConfirm={() => close(true)}
        onClose={() => dispatch(receivingResolutionDismissed())}
        testId="shortage-sheet"
      />

      <ConfirmSheet
        isOpen={Boolean(overCount)}
        title="¿Llegaron de más?"
        message="Vas a registrar más de lo que falta por recibir."
        confirmLabel="Registrar igual"
        cancelLabel={step === RECEIVING_STEP.QUANTITY ? 'Corregir la cantidad' : 'Cancelar'}
        busy={pending === 'count'}
        busyLabel="Registrando…"
        onConfirm={() => dispatch(confirmExcessCount())}
        onClose={correctCount}
        testId="receiving-excess-sheet"
        confirmTestId="receiving-excess-confirm"
      >
        {overCount && (
          <ul className="divide-y divide-border rounded-2xl border border-border">
            {overCount.excess.map((item) => (
              <ExcessDetail
                key={item.line_id}
                item={item}
                name={item.name || lineOf(item.line_id)?.product_name}
                count={item.line_id === overCount.arg.lineId ? overCount.arg : undefined}
              />
            ))}
          </ul>
        )}
      </ConfirmSheet>
    </div>
  );
};
